import React, { useEffect, useState, useMemo } from 'react';
import './LogList.css';
import LogModal from './LogDetailModal';
import { LogType } from '../interfaces/LogType';
import { getCityName } from '../utils/geocoding';
import { FlightLogEntry } from '../interfaces/FlightLogEntry';
import {
  deleteLogById,
  getLogCount,
  loadLogs,
  saveLogs,
} from '../firebase/db-logs';
import { SettingsType, UserType } from '../interfaces/SettingsType';
import { FilterType } from '../interfaces/FilterType';
import LoadingSpinner from './LoadingSpinner';
import ExportCSVButton from './ExportCSVButton';

interface LogListProps {
  settings: SettingsType;
  itemsPerPage?: number;
  pilots: UserType[];
  isAdmin?: boolean;
  droneType?: string | number;
  pilotId?: string;
  showDroneId?: boolean;
  filters?: FilterType[];
}

const LogList: React.FC<LogListProps> = ({
  settings,
  itemsPerPage = 10,
  pilots,
  isAdmin = false,
  droneType = undefined,
  pilotId = undefined,
  showDroneId = false,
  filters = [],
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [logs, setLogs] = useState<LogType[]>([]);
  const [selectedLog, setSelectedLog] = useState<LogType | null>(null);
  const [selectedLogNum, setSelectedLogNum] = useState<number>(0);
  const [locations, setLocations] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [logCount, setLogCount] = useState<number>(0);

  const [sortColumn, setSortColumn] = useState<string>('flightNumber');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [logsWithFlightNumbers, setLogsWithFlightNumbers] = useState<any[]>([]);
  const [sortedLogs, setSortedLogs] = useState<any[]>([]);

  useEffect(() => {
    const sortedByDate = logs
      .slice()
      .sort(
        (a, b) =>
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      );

    const numberedLogs = sortedByDate.map((log, index) => ({
      ...log,
      flightNumber: index + 1,
    }));
    setLogsWithFlightNumbers(numberedLogs.reverse());
  }, [logs]);

  useEffect(() => {
    loadData();
  }, [pilotId, filters, itemsPerPage]); 
    

  useEffect(() => {
    async function fetchLocations() {
      const updatedLogs: LogType[] = [];
      const locationPromises = logsWithFlightNumbers.map(async (log) => {
        if (!log.flight || log.flight.length === 0) return null;

        const flightLogDataTyped = log.flight as FlightLogEntry[];
        const firstCoordinate = flightLogDataTyped[0];

        let cityName = log.location;

        if (!cityName || cityName === '') {
          if (
            firstCoordinate &&
            firstCoordinate.latitude &&
            firstCoordinate.longitude
          ) {
            try {
              cityName = await getCityName(
                firstCoordinate.latitude,
                firstCoordinate.longitude
              );

              log.location = cityName;

              updatedLogs.push(log);
            } catch (error) {
              console.error(
                `Fehler beim Abrufen des Orts für Log ${log.logId}:`,
                error
              );
              cityName = 'Ort unbekannt';
            }
          } else {
            cityName = 'Ort unbekannt';
          }
        }

        return { logId: log.logId, cityName };
      });

      const resolvedLocations = await Promise.all(locationPromises);
      const locationMap: Record<string, string> = {};

      resolvedLocations.forEach((loc) => {
        if (loc) {
          locationMap[loc.logId] = loc.cityName;
        }
      });

      setLocations(locationMap);

      if (updatedLogs.length > 0) {
        try {
          await saveLogs(updatedLogs);
          console.log('Logs erfolgreich aktualisiert');
        } catch (error) {
          console.error(
            'Fehler beim Speichern der aktualisierten Logs:',
            error
          );
        }
      }
    }

    fetchLocations();
  }, [logsWithFlightNumbers, logs]);

  useEffect(() => {
    if (logsWithFlightNumbers == undefined) setSortedLogs([]);
    const sorted = logsWithFlightNumbers.slice().sort((a, b) => {
      let compareResult = 0;
      switch (sortColumn) {
        case 'flightNumber':
          compareResult = a.flightNumber - b.flightNumber;
          break;
        case 'date':
          compareResult =
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
          break;
        case 'flightDuration':
          const durationA =
            new Date(a.endTime).getTime() - new Date(a.startTime).getTime();
          const durationB =
            new Date(b.endTime).getTime() - new Date(b.startTime).getTime();
          compareResult = durationA - durationB;
          break;
        case 'isMission':
          compareResult =
            a.isMission === b.isMission ? 0 : a.isMission ? -1 : 1;
          break;
        default:
          break;
      }
      return sortDirection === 'asc' ? compareResult : -compareResult;
    });
    setSortedLogs(sorted);
  }, [logsWithFlightNumbers, sortColumn, sortDirection]);

  useEffect(() => {
    loadData();
  }, [pilotId, filters]);
  async function loadData() {
    console.log('load');
    setCurrentPage(1);
    const tempCount = await getLogCount(filters);
    setTotalPages(Math.ceil(tempCount / itemsPerPage));
    setLogCount(tempCount);
    await loadInitPage();

    console.log('load finished');
  }
  async function loadInitPage() {
    setIsLoading(true);
    const tempLogs = await loadLogs(itemsPerPage, undefined, filters);
    if (tempLogs == undefined) {
      return;
    }
    setLogs(tempLogs);
    setIsLoading(false);
  }
  async function loadNextPage() {
    setIsLoading(true);
    const tempLogs = await loadLogs(
      itemsPerPage,
      logs[logs.length - 1],
      filters
    );
    if (tempLogs == undefined) {
      return;
    }
    setLogs((currentLogs) => [...currentLogs, ...tempLogs]);
    setIsLoading(false);
  }

  const openModal = (log: LogType, logNumber: number) => {
    setSelectedLog(log);
    setSelectedLogNum(logNumber);
  };
  const onCloseModal = async (log: LogType | undefined) => {
    if (log != undefined) {
      console.log(log);
      await saveLogs([log]);
      const tempLogs = [...logs];
      const index = tempLogs.findIndex(
        (element) => element.logId === log.logId
      );
      tempLogs[index] = log;
      setLogs(tempLogs);
    } else if (selectedLog != undefined) {
      await deleteLogById(selectedLog.logId);
      const tempLogs = logs.filter(
        (element) => element.logId !== selectedLog.logId
      );
      setLogs(tempLogs);
    }
    setSelectedLog(null);
    setSelectedLogNum(0);
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const calculateDuration = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationSeconds = Math.floor(
      (end.getTime() - start.getTime()) / 1000
    );
    const minutes = Math.floor(durationSeconds / 60);
    const seconds = durationSeconds % 60;
    return `${minutes}m ${seconds}s`;
  };

  const formatDate = (date: Date) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const formatTime = (date: Date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };
  /*
  const sortedLogs = useMemo(() => {
    if (logsWithFlightNumbers == undefined) return [];
    const sorted = logsWithFlightNumbers.slice().sort((a, b) => {
      let compareResult = 0;
      switch (sortColumn) {
        case 'flightNumber':
          compareResult = a.flightNumber - b.flightNumber;
          break;
        case 'date':
          compareResult =
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
          break;
        case 'flightDuration':
          const durationA =
            new Date(a.endTime).getTime() - new Date(a.startTime).getTime();
          const durationB =
            new Date(b.endTime).getTime() - new Date(b.startTime).getTime();
          compareResult = durationA - durationB;
          break;
        case 'isMission':
          compareResult =
            a.isMission === b.isMission ? 0 : a.isMission ? -1 : 1;
          break;
        default:
          break;
      }
      return sortDirection === 'asc' ? compareResult : -compareResult;
    });
    return sorted;
  }, [logsWithFlightNumbers, sortColumn, sortDirection]);*/

  const goToNextPage = async () => {
    if (currentPage < totalPages) {
      if (logs.length < itemsPerPage * currentPage + 1) {
        await loadNextPage();
      }
      setCurrentPage((prev) => prev + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const getDroneName = (type: any) => {
    switch (type) {
      case 69:
        return 'DJI Mavic 2EA';
      case 'Matrice350RTK':
        return 'DJI Matrice 350 RTK';
      default:
        return 'Eigene Drohne';
    }
  };

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  let computedColSpan = 9; 
  if (windowWidth <= 480) {
    computedColSpan = 4; 
  } else if (windowWidth <= 600) {
    computedColSpan = 5; 
  } else if (windowWidth <= 900) {
    computedColSpan = 6; 
  } else if (windowWidth <= 1200) {
    computedColSpan = 7; 
  } else {
    computedColSpan = 9;
  }

  return (
    <>
      <div className="log-table-container">
        <table className="log-table">
          <thead>
            <tr>
              <th
                className="clickable-header"
                onClick={() => handleSort('flightNumber')}
              >
                <div>Flug</div>
              </th>
              <th
                className="clickable-header"
                onClick={() => handleSort('date')}
              >
                <div>Datum</div>
              </th>
              <th>
                <div>Ort</div>
              </th>
              <th>
                <div>Abflug</div>
              </th>
              <th>
                <div>Landung</div>
              </th>
              <th
                className="clickable-header"
                onClick={() => handleSort('flightDuration')}
              >
                <div>Flugdauer</div>
              </th>
              <th
                className="clickable-header"
                onClick={() => handleSort('isMission')}
              >
                <div>Art</div>
              </th>
              <th>
                <div>Sicht</div>
              </th>
              <th>
                <div>{showDroneId ? 'Drohne' : 'Pilot'}</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && sortedLogs.length === 0 && (
              <tr>
                <td
                  colSpan={computedColSpan}
                  style={{ textAlign: 'center', padding: '20px' }}
                >
                  <img
                    src="/img/no-logs.svg"
                    alt="No logs available"
                    style={{ width: '150px', marginBottom: '20px' }}
                  />
                  <p>Keine Fluglogs gefunden</p>
                </td>
              </tr>
            )}
            {sortedLogs
              .slice(
                (currentPage - 1) * itemsPerPage,
                currentPage * itemsPerPage
              )
              .map((log, index) => {
                const startDate = new Date(log.startTime);
                const endDate = new Date(log.endTime);
                const location = locations[log.logId] || 'Lädt...';

                const formattedDate = formatDate(startDate);
                const formattedStartTime = formatTime(startDate);
                const formattedEndTime = formatTime(endDate);
                const logNumber = log.logNr;

                const flightNumber =
                  logCount - index - (currentPage - 1) * itemsPerPage;

                return (
                  <tr
                    key={log.logId}
                    onClick={() => openModal(log, logNumber)}
                    className="clickable-row"
                  >
                    <td>
                      <div className="cell-content">{logNumber}</div>
                    </td>
                    <td>
                      <div className="cell-content">{formattedDate}</div>
                    </td>
                    <td>
                      <div className="cell-content">{location}</div>
                    </td>
                    <td>
                      <div className="cell-content">{formattedStartTime}</div>
                    </td>
                    <td>
                      <div className="cell-content">{formattedEndTime}</div>
                    </td>
                    <td>
                      <div className="cell-content">
                        {calculateDuration(log.startTime, log.endTime)}
                      </div>
                    </td>
                    <td>
                      <div className="cell-content">{log.mission}</div>
                    </td>
                    <td>
                      <div className="cell-content">{log.flightType}</div>
                    </td>
                    <td>
                      <div className="cell-content">
                        <div className="cell-content">
                          {showDroneId
                            ? getDroneName(log.droneType)
                            : pilots.some(
                                (pilot) => pilot.email === log.pilotId
                              )
                            ? pilots
                                .filter((pilot) => pilot.email === log.pilotId)
                                .map(
                                  (pilot) =>
                                    `${pilot.prename[0]}.${pilot.lastname}`
                                )
                            : 'N/A'}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button
          className="pagination-button"
          onClick={goToPreviousPage}
          disabled={currentPage === 1}
        >
          <span className="material-icons-pagination">arrow_back</span>
        </button>
        <span className="pagination-info">
          Seite {currentPage} von {totalPages}
        </span>
        <button
          className="pagination-button"
          onClick={goToNextPage}
          disabled={currentPage === totalPages || isLoading}
        >
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <span className="material-icons-pagination">arrow_forward</span>
            </>
          )}
        </button>
      </div>
      {isAdmin && <ExportCSVButton isFixed={true} data={sortedLogs} />}

      {selectedLog && (
        <LogModal
        settings={settings}
          log={selectedLog}
          logNumber={selectedLogNum}
          onClose={onCloseModal}
          pilots={[{ email: 'N/A', prename: 'N/A', lastname: '' }, ...pilots]}
          isAdmin={isAdmin}
        />
      )}
    </>
  );
};

export default LogList;
